import React from 'react'
import { observer } from 'mobx-react-lite'
import moment from 'moment'

// evertel
import { Card, CardHeader, CardBody, Text } from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { useObjectUrl } from '@evertel/hooks'
import { CurrentUserController } from '@evertel/blue-user'
import { labelFromValue } from '@evertel/utils'
import { DocumentTileField } from '@evertel/types'
import { DocumentTileBody } from '../../elements'
import { ParsedItem } from '../../schema-builder-dnd/types'
import { SchemaBuilderController } from '../../../controller'

const SAMPLE_NAME = 'Sample document name'
const SAMPLE_TEXT = '000000-00'
const SAMPLE_MULTILINE = 'Sample text for a multiline field. Longer answers are cut off after two lines so the tile keeps a consistent size.'
const SAMPLE_ADDRESS = '0000 Main St, Sample Address'

interface RoomTilePreviewCardProps {
    fields: ParsedItem[]
    schemaBuilderController: SchemaBuilderController
}

const RoomTilePreviewCard: React.FC<RoomTilePreviewCardProps> = observer(({
    fields,
    schemaBuilderController
}) => {

    const currentUserController = useService(CurrentUserController, [])

    // an icon picked but not saved yet only exists as a local file
    const stagedIconUrl = useObjectUrl(schemaBuilderController.iconFile)

    const schema = {
        name: schemaBuilderController.name,
        icon: schemaBuilderController.icon,
        color: schemaBuilderController.color,
        iconMedia: (stagedIconUrl) ? { url: stagedIconUrl } : schemaBuilderController.iconMedia
    }

    const tileFields: DocumentTileField[] = fields.map(field => ({
        fieldId: field.id,
        label: field.title as string,
        value: sampleValue(field, currentUserController.fullName)
    }))

    return (
        <Card className="room-tile-panel">
            <CardHeader>
                <Text
                    tag="h5"
                    bold
                    className="mb-0">
                    Preview tile in Room
                </Text>
            </CardHeader>
            <CardBody>
                <Text
                    tag="p"
                    size={12}
                    color="muted">
                    Shown with sample data.
                </Text>
                <DocumentTileBody
                    name={SAMPLE_NAME}
                    schema={schema}
                    departmentShortName={currentUserController.selectedDepartment?.shortName}
                    fields={tileFields}
                    // whoever shares a new document owns it, and it was just updated
                    ownerName="Me"
                    updatedDate={new Date()}
                />
            </CardBody>
        </Card>
    )
})

const optionLabel = (field: ParsedItem, index: number) => {
    const option = (field.options as { label?: string, value?: string }[] | undefined)?.[index]
    if (!option) return undefined

    return option.label?.trim() || labelFromValue(option.value ?? '')
}

// filler values, never real document data: the preview is there to show the layout the
// chosen fields produce, so what matters is that the lengths are realistic
const sampleValue = (field: ParsedItem, userName: string): string | string[] => {
    switch (field.type) {
        case 'date':
            return moment().format('L')

        case 'map':
            return SAMPLE_ADDRESS

        case 'user':
            return userName

        case 'dropdown':
            return optionLabel(field, 0) || SAMPLE_TEXT

        case 'checkbox':
        case 'checkbox-group':
            return [optionLabel(field, 0), (field.allowMultiple) ? optionLabel(field, 1) : undefined].filter(Boolean) as string[]

        default:
            return (field.multiline) ? SAMPLE_MULTILINE : ((field.placeholder as string) || SAMPLE_TEXT)
    }
}

export { RoomTilePreviewCard }
